import { onMounted, onUnmounted, ref, shallowRef, type Ref } from 'vue';

/**
 * Opens a camera stream into a <video> on mount and stops it on unmount. Offers the torch where
 * the device has one. `handleFailed` runs when the camera cannot be opened (no permission, no camera).
 */
export function useCameraStream(
  videoRef: Ref<HTMLVideoElement | null>,
  constraints: MediaStreamConstraints,
  handleFailed: () => void,
) {
  const stream = shallowRef<MediaStream | null>(null);
  const hasTorch = ref(false);
  const isTorchOn = ref(false);
  let isUnmounted = false;

  const getVideoTrack = () => stream.value?.getVideoTracks()[0];

  const openStream = async () => {
    try {
      const opened = await navigator.mediaDevices.getUserMedia(constraints);
      // Closed while the permission prompt or camera start was pending: release it right away.
      if (isUnmounted) {
        opened.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.value = opened;
      if (videoRef.value) videoRef.value.srcObject = stream.value;
      hasTorch.value = !!getVideoTrack()?.getCapabilities?.().torch;
    } catch {
      if (!isUnmounted) handleFailed();
    }
  };

  const toggleTorch = async () => {
    const track = getVideoTrack();
    if (!track?.getCapabilities?.().torch) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !isTorchOn.value }] });
      isTorchOn.value = !isTorchOn.value;
    } catch {
      // Some devices report a torch but refuse to switch it; the camera keeps working without.
    }
  };

  onMounted(() => void openStream());
  /** Some devices leave the torch on after the stream stops unless it is switched off first. */
  const stopStream = async () => {
    const tracks = stream.value?.getTracks() ?? [];
    if (isTorchOn.value) {
      await getVideoTrack()
        ?.applyConstraints({ advanced: [{ torch: false }] })
        .catch(() => undefined);
    }
    tracks.forEach((track) => track.stop());
  };

  onUnmounted(() => {
    isUnmounted = true;
    void stopStream();
  });

  return { stream, hasTorch, isTorchOn, toggleTorch };
}
